import React, { FC, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  MainLoader, Modal, ModalProps, PrimaryButton, ResourceLoader, T2, useScopedSelector, SecondaryButton,
} from 'ia-react-core';
import { useTranslation } from 'react-i18next';
import redirectTo from '../../utils/redirectTo';
import TableOfContentsResource from '../TableOfContentsResource';
import { TableOfContents } from '../../interfaces/TableOfContents';
import { TableOfContentsResourceInfo } from '../TableOfContentsResource/TableOfContentsResource';
import Items from '../Items';
import { StyledActionBar, StyledAside, StyledSafeHtml } from './TermsOfUseModal.styles';
import DropDownLanguage from './DropDownLanguage';

export interface TermsOfUseModalProps extends ModalProps {
  url: string;
  showGlobalConsents: boolean;
  onConfirm?: () => void;
}

const TermsOfUseModal: FC<TermsOfUseModalProps> = ({ url, showGlobalConsents, onConfirm, ...props }) => {
  const { t } = useTranslation('TermsOfUseModal');
  const navigate = useNavigate();
  const tableOfContentName = 'terms-modal';

  let tableOfContentsData: TableOfContents = useScopedSelector(`${TableOfContentsResourceInfo.dataDst}-${tableOfContentName}`);

  if (showGlobalConsents) {
    tableOfContentsData = { ...tableOfContentsData };
  } else {
    tableOfContentsData = { ...tableOfContentsData, items: tableOfContentsData?.items.filter((x) => (x.fields.queryParameters !== 'show-global-consent=true')) };
  }

  useEffect(() => {
    const element = document.createElement('div');
    element.innerHTML = tableOfContentsData?.title;
    document.title = element.textContent;
  }, [tableOfContentsData?.title]);

  return (
    <Modal {...props} name="termsOfUseModal" isClosable={false}>
      <TableOfContentsResource
        autoRequest
        url={url}
        onFailure={() => redirectTo('/technical-error', navigate)}
        requestOnLanguageChange
        tableOfContentName={tableOfContentName}
      />
      <DropDownLanguage />
      <T2 data-testid="termsOfUseTitle">{t('modalTitle')}</T2>
      <ResourceLoader
        resourceSrc={`${TableOfContentsResourceInfo.name}-${tableOfContentName}`}
        CustomLoader={MainLoader}
      >
        <StyledAside data-testid="termsOfUseData">
          <StyledSafeHtml>{tableOfContentsData.introBody}</StyledSafeHtml>
          <Items data={tableOfContentsData} isModalItem />
        </StyledAside>
        <StyledActionBar>
          <PrimaryButton data-testid="acceptTermsOfUseButton" onClick={onConfirm}>{t('acceptButton')}</PrimaryButton>
          <SecondaryButton data-testid="declineTermsOfUseButton" onClick={() => { redirectTo('/log-off', navigate); }}>{t('declineButton')}</SecondaryButton>
        </StyledActionBar>
      </ResourceLoader>
    </Modal>
  );
};

export default TermsOfUseModal;
